// ./src/components/features/Connectors/ConfigEditors/SendGridConnectorConfigEditor.tsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Stack,
  TextInput,
  Text,
  Anchor,
  Alert,
  Switch,
  Group,
  Box,
  List,
  Button,
  ActionIcon,
  Paper,
} from "@mantine/core";
import {
  IconInfoCircle,
  IconMail,
  IconAlertTriangle,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import { stringifyObject, parseObject } from "@/utils";
import { SendGridConnectorConfig } from "@/types";
import { AccountEmailSelector } from "@/components/features/AccountEmails/AccountEmailSelector";

export interface SendGridConnectorConfigEditorProps {
  /** JSON string of connector configuration */
  value: string;

  /** Callback when configuration changes */
  onChange: (value: string) => void;

  /** Whether the editor is disabled */
  disabled?: boolean;
}

// Default empty configuration
const DEFAULT_CONFIG: SendGridConnectorConfig = {
  apiKey: "",
  fromAccountEmailId: "",
  categories: [],
  customArgs: {},
  sandboxMode: false,
};

/**
 * Parse and validate SendGrid connector configuration
 */
const parseSendGridConfig = (jsonString: string): SendGridConnectorConfig => {
  const parsed = parseObject(jsonString, DEFAULT_CONFIG);
  // Ensure arrays and objects exist
  if (!parsed.categories) parsed.categories = [];
  if (!parsed.customArgs) parsed.customArgs = {};
  return parsed;
};

export const SendGridConnectorConfigEditor: React.FC<
  SendGridConnectorConfigEditorProps
> = ({ value, onChange, disabled = false }) => {
  // Parse incoming value to config state
  const [config, setConfig] = useState<SendGridConnectorConfig>(() =>
    parseSendGridConfig(value)
  );

  // Local state for adding new category
  const [newCategory, setNewCategory] = useState("");

  // Local state for adding new custom arg
  const [newArgKey, setNewArgKey] = useState("");
  const [newArgValue, setNewArgValue] = useState("");

  // Sync config when external value changes
  useEffect(() => {
    const parsed = parseSendGridConfig(value);
    setConfig(parsed);
  }, [value]);

  // Update parent when config changes
  const updateConfig = useCallback(
    (newConfig: SendGridConnectorConfig) => {
      setConfig(newConfig);
      onChange(stringifyObject(newConfig, DEFAULT_CONFIG));
    },
    [onChange]
  );

  // Handle field changes
  const handleApiKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, apiKey: e.target.value });
  };

  const handleFromAccountEmailIdChange = (accountEmailId: string | null) => {
    updateConfig({ ...config, fromAccountEmailId: accountEmailId || "" });
  };

  const handleSandboxModeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, sandboxMode: e.currentTarget.checked });
  };

  // Categories management
  const handleAddCategory = () => {
    const trimmed = newCategory.trim();
    if (trimmed && !(config.categories || []).includes(trimmed)) {
      const categories = [...(config.categories || []), trimmed].slice(0, 10); // Max 10
      updateConfig({ ...config, categories });
      setNewCategory("");
    }
  };

  const handleRemoveCategory = (index: number) => {
    const categories = [...(config.categories || [])];
    categories.splice(index, 1);
    updateConfig({ ...config, categories });
  };

  // Custom args management
  const handleAddCustomArg = () => {
    const key = newArgKey.trim();
    const val = newArgValue.trim();
    if (key && val) {
      const customArgs = { ...(config.customArgs || {}), [key]: val };
      updateConfig({ ...config, customArgs });
      setNewArgKey("");
      setNewArgValue("");
    }
  };

  const handleRemoveCustomArg = (key: string) => {
    const customArgs = { ...(config.customArgs || {}) };
    delete customArgs[key];
    updateConfig({ ...config, customArgs });
  };

  return (
    <Stack gap="md">
      {/* Setup Instructions */}
      <Alert icon={<IconMail size={16} />} color="blue" variant="light">
        <Text size="sm" fw={500} mb="xs">
          Setup Instructions:
        </Text>
        <List size="sm" spacing={4}>
          <List.Item>
            Go to{" "}
            <Anchor
              href="https://app.sendgrid.com/settings/api_keys"
              target="_blank"
              rel="noopener noreferrer"
            >
              SendGrid → Settings → API Keys
            </Anchor>
          </List.Item>
          <List.Item>
            Click <strong>Create API Key</strong> with "Mail Send" permission
          </List.Item>
          <List.Item>
            Verify your sender email in{" "}
            <Anchor
              href="https://app.sendgrid.com/settings/sender_auth"
              target="_blank"
              rel="noopener noreferrer"
            >
              Sender Authentication
            </Anchor>
          </List.Item>
        </List>
      </Alert>

      {/* API Key Field */}
      <TextInput
        label="API Key"
        placeholder="SG.xxxxxxxxxxxxxxxxxxxxxxxx"
        description="SendGrid API Key with Mail Send permission"
        value={config.apiKey}
        onChange={handleApiKeyChange}
        disabled={disabled}
        required
        size="md"
        type="password"
        autoComplete="off"
      />

      {/* From Account Email Field */}
      <AccountEmailSelector
        label="Sender Email"
        placeholder="Select a verified email"
        description="Choose a confirmed account email to use as the sender address. Must also be verified in SendGrid."
        value={config.fromAccountEmailId}
        onChange={handleFromAccountEmailIdChange}
        disabled={disabled}
        required
        size="md"
        confirmedOnly
        emptyText="No confirmed emails available"
      />

      {/* Categories Section */}
      <Box>
        <Text size="sm" fw={500} mb="xs">
          Categories (Optional)
        </Text>
        <Text size="xs" c="dimmed" mb="sm">
          Used for email classification and analytics in SendGrid (max 10)
        </Text>

        {/* Existing categories */}
        {(config.categories || []).length > 0 && (
          <Stack gap="xs" mb="sm">
            {(config.categories || []).map((category, index) => (
              <Group key={index} gap="xs">
                <Paper withBorder p="xs" style={{ flex: 1 }} bg="gray.0">
                  <Text size="sm">{category}</Text>
                </Paper>
                <ActionIcon
                  color="red"
                  variant="subtle"
                  onClick={() => handleRemoveCategory(index)}
                  disabled={disabled}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            ))}
          </Stack>
        )}

        {/* Add new category */}
        {(config.categories || []).length < 10 && (
          <Group gap="xs">
            <TextInput
              placeholder="e.g., form-submission"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              disabled={disabled}
              size="sm"
              style={{ flex: 1 }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddCategory();
                }
              }}
            />
            <Button
              size="sm"
              variant="light"
              leftSection={<IconPlus size={14} />}
              onClick={handleAddCategory}
              disabled={disabled || !newCategory.trim()}
            >
              Add
            </Button>
          </Group>
        )}
      </Box>

      {/* Custom Args Section */}
      <Box>
        <Text size="sm" fw={500} mb="xs">
          Custom Arguments (Optional)
        </Text>
        <Text size="xs" c="dimmed" mb="sm">
          Key-value pairs passed to SendGrid webhooks. Values support{" "}
          <code>{"{{varName}}"}</code> placeholders.
        </Text>

        {/* Existing custom args */}
        {Object.keys(config.customArgs || {}).length > 0 && (
          <Stack gap="xs" mb="sm">
            {Object.entries(config.customArgs || {}).map(([key, val]) => (
              <Group key={key} gap="xs">
                <Paper withBorder p="xs" style={{ flex: 1 }} bg="gray.0">
                  <Text size="sm">
                    <strong>{key}:</strong> {val}
                  </Text>
                </Paper>
                <ActionIcon
                  color="red"
                  variant="subtle"
                  onClick={() => handleRemoveCustomArg(key)}
                  disabled={disabled}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            ))}
          </Stack>
        )}

        {/* Add new custom arg */}
        <Group gap="xs" align="flex-end">
          <TextInput
            placeholder="Key"
            value={newArgKey}
            onChange={(e) => setNewArgKey(e.target.value)}
            disabled={disabled}
            size="sm"
            style={{ flex: 1 }}
          />
          <TextInput
            placeholder="Value (e.g., {{formId}})"
            value={newArgValue}
            onChange={(e) => setNewArgValue(e.target.value)}
            disabled={disabled}
            size="sm"
            style={{ flex: 2 }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleAddCustomArg();
              }
            }}
          />
          <Button
            size="sm"
            variant="light"
            leftSection={<IconPlus size={14} />}
            onClick={handleAddCustomArg}
            disabled={disabled || !newArgKey.trim() || !newArgValue.trim()}
          >
            Add
          </Button>
        </Group>
      </Box>

      {/* Sandbox Mode Toggle */}
      <Box>
        <Group justify="space-between">
          <div>
            <Text size="sm">Sandbox Mode</Text>
            <Text size="xs" c="dimmed">
              Test mode - emails won't actually be sent
            </Text>
          </div>
          <Switch
            checked={config.sandboxMode === true}
            onChange={handleSandboxModeChange}
            disabled={disabled}
          />
        </Group>
      </Box>

      {/* Sandbox Warning */}
      {config.sandboxMode && (
        <Alert
          icon={<IconAlertTriangle size={16} />}
          color="orange"
          variant="light"
        >
          <Text size="sm">
            <strong>Sandbox mode is enabled.</strong> Emails will be validated
            but not delivered. Disable for production use.
          </Text>
        </Alert>
      )}

      {/* Info about usage */}
      <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light">
        <Text size="sm" fw={500} mb="xs">
          How it works:
        </Text>
        <List size="sm" spacing={4}>
          <List.Item>
            This connector configures the SendGrid connection settings
          </List.Item>
          <List.Item>
            Recipients, subject, and body are configured in the workflow
          </List.Item>
          <List.Item>
            Custom args are included in SendGrid webhook events for tracking
          </List.Item>
        </List>
      </Alert>
    </Stack>
  );
};
